import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, HardDrive, Trash2, RefreshCw } from "lucide-react";

export default function DataExport() {
  const [busy, setBusy] = useState(false);
  const [includeDocs, setIncludeDocs] = useState(true);
  const [backups, setBackups] = useState({ backups: [], storage: "", location: "" });

  const loadBackups = async () => {
    try {
      const r = await api.get("/admin/backups");
      setBackups(r.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadBackups(); }, []);

  const doExport = async () => {
    setBusy(true);
    try {
      const url = `/admin/export/full?include_documents=${includeDocs}`;
      // Force full URL + auth-cookie fetch → blob download
      const r = await api.get(url, { responseType: "blob", timeout: 300000 });
      const blob = new Blob([r.data], { type: "application/zip" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = (r.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || "hcd_export.zip";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally { setBusy(false); }
  };

  const del = async (name) => {
    if (!confirm(`Delete backup ${name}?`)) return;
    try { await api.delete(`/admin/backups/${encodeURIComponent(name)}`); loadBackups(); toast.success("Deleted"); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="data-export">
      <div className="flex items-center gap-3 mb-6">
        <HardDrive className="w-6 h-6 text-[#005289]" />
        <div>
          <h1 className="text-xl font-bold">Data Export & Backups</h1>
          <p className="text-sm text-slate-500">One-click full export of the entire system, plus a view of nightly backups.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6" data-testid="full-export-panel">
        <div className="font-semibold mb-3">Full system export</div>
        <div className="text-xs text-slate-600 mb-4">
          Downloads a ZIP containing <b>contracts.csv/json</b>, <b>audit.csv</b>, <b>lineage.csv</b>, <b>users.csv</b>, all original documents, and a <b>DATA_DICTIONARY.md</b>.
        </div>
        <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
          <input type="checkbox" checked={includeDocs} onChange={e => setIncludeDocs(e.target.checked)} data-testid="export-include-docs" />
          Include document files (larger export)
        </label>
        <button onClick={doExport} disabled={busy} className="h-10 px-5 bg-[#005289] text-white text-sm font-semibold rounded flex items-center gap-2 disabled:opacity-50" data-testid="export-download-btn">
          <Download className="w-4 h-4" /> {busy ? "Building export…" : "Download full export"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md" data-testid="backups-list">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <div className="font-semibold">Nightly backups</div>
            <div className="text-[11px] text-slate-500">Storage: <b>{backups.storage}</b> · Location: <span className="font-mono">{backups.location}</span></div>
          </div>
          <button className="text-xs px-2 py-1 border border-slate-200 rounded flex items-center gap-1" onClick={loadBackups}>
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        {backups.backups.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">No backups yet. Nightly job runs at 2am UTC.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs">
              <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Size</th><th className="text-left px-3 py-2">Last modified</th><th className="text-right px-3 py-2">Actions</th></tr>
            </thead>
            <tbody>
              {backups.backups.map(b => (
                <tr key={b.name} className="border-b border-slate-100" data-testid={`backup-row-${b.name}`}>
                  <td className="px-3 py-2 font-mono text-xs">{b.name}</td>
                  <td className="px-3 py-2">{(b.size / 1024 / 1024).toFixed(2)} MB</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{b.last_modified?.slice(0, 19).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-right">
                    <button className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50" onClick={() => del(b.name)} data-testid={`backup-delete-${b.name}`}>
                      <Trash2 className="w-3 h-3 inline" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
